import React, { Fragment } from "react";
import Webcam from "react-webcam";
import { SITENAMEALIAS, LOGO } from "../utils/init";
import { showToast } from "../utils/library";
import Loader from "../components/Loader";
import "./kyc.css";

/**
 * KycCapturePhoto - Standalone page for mobile photo capture via QR code.
 *
 * URL format (path params, base64 encoded):
 *   /kyc-capture-photo/<base64_encoded_uuid>/<base64_encoded_token>
 *
 * Flow:
 *   1. User scans QR code on desktop KYC page
 *   2. Opens this page on mobile browser
 *   3. Captures selfie with mobile camera
 *   4. Face detection validates the photo
 *   5. Photo is uploaded via API
 */

// Updated to match Next.js reference API base URL
const WEBSERVICE =
  "https://institutional-bo.paybito.com:8443/BrokerAdminApi";

class KycCapturePhoto extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showLoader: false,
      uuid: "",
      token: "",
      paramsValid: false,
      paramsError: "",
      cameraReady: false,
      capturedPhoto: null,
      faceDetected: false,
      faceDetectionLoading: false,
      uploadSuccess: false,
      uploadError: "",
    };

    this.webcamRef = React.createRef();
    this.canvasRef = React.createRef();
    this.faceApiLoaded = false;
    this.faceapi = null;
    this.lastDetectedTime = Date.now();
    this.detectInterval = null;
  }

  componentDidMount() {
    this.parseUrlParams();
    this.loadFaceApiModels();
  }

  componentWillUnmount() {
    if (this.detectInterval) {
      clearInterval(this.detectInterval);
      this.detectInterval = null;
    }
  }

  /* ============================
     URL PARAMETER PARSING
     ============================ */

  /**
   * Safely decode a base64 string, handling URL-safe variants and padding.
   * Matches the safeBase64Decode helper from the Next.js reference.
   */
  safeBase64Decode = (str) => {
    if (!str) return null;
    try {
      // URL decode first in case it was URL-encoded
      let decodedStr = str;
      try {
        decodedStr = decodeURIComponent(str);
      } catch {
        decodedStr = str;
      }

      // Replace URL-safe base64 characters back to standard base64
      const base64 = decodedStr.replace(/-/g, "+").replace(/_/g, "/");

      // Add padding if necessary
      const paddedBase64 = base64.padEnd(
        base64.length + ((4 - (base64.length % 4)) % 4),
        "="
      );

      return atob(paddedBase64);
    } catch (error) {
      console.error("Base64 decode error:", error, "Input:", str);
      return null;
    }
  };

  parseUrlParams = () => {
  try {
    let encodedUuid = null;
    let encodedToken = null;

    // --- Primary: Read from URL path segments ---
    // QR URL format: /payments/kyc-capture-photo/<base64_uuid>/<base64_token>
    const pathname = window.location.pathname;
    const segments = pathname.split("/").filter(Boolean);

    // Check for the exact pattern: ["payments", "kyc-capture-photo", "uuid", "token"]
    if (
      segments.length >= 4 && 
      segments[0] === "payments" && 
      segments[1] === "kyc-capture-photo"
    ) {
      encodedUuid = segments[2];
      encodedToken = segments[3];
    }
    // Alternative: if it's just /kyc-capture-photo/uuid/token (without payments)
    else if (
      segments.length >= 3 && 
      segments[0] === "kyc-capture-photo"
    ) {
      encodedUuid = segments[1];
      encodedToken = segments[2];
    }

    // --- Fallback: try query params (legacy support) ---
    if (!encodedUuid || !encodedToken) {
      const urlParams = new URLSearchParams(window.location.search);
      encodedUuid = urlParams.get("uuid") || encodedUuid;
      encodedToken = urlParams.get("token") || encodedToken;
    }

    if (!encodedUuid || !encodedToken) {
      this.setState({
        paramsValid: false,
        paramsError:
          "Missing required parameters. Please scan the QR code again.",
      });
      return;
    }

    // Use safeBase64Decode
    const uuid = this.safeBase64Decode(encodedUuid);
    const token = this.safeBase64Decode(encodedToken);

    console.log("Decoded params => uuid:", uuid, "token:", token ? "***" : null);
    console.log("URL segments:", segments); // Add this for debugging

    if (!uuid || !token) {
      this.setState({
        paramsValid: false,
        paramsError:
          "Invalid parameters. Please scan the QR code again.",
      });
      return;
    }

    this.setState({ uuid, token, paramsValid: true });
  } catch (err) {
    console.error("Error parsing URL params:", err);
    this.setState({
      paramsValid: false,
      paramsError:
        "Invalid QR code data. Please scan the QR code again.",
    });
  }
};

  /* ============================
     FACE API
     ============================ */

  loadFaceApiModels = async () => {
    try {
      const faceapi = await import("face-api.js");
      const MODEL_URL = process.env.PUBLIC_URL + "/models";
      await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
      await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
      this.faceapi = faceapi;
      this.faceApiLoaded = true;
      console.log("Face-api models loaded");
      // Start face detection loop once models are ready
      this.startFaceDetection();
    } catch (err) {
      console.error("Error loading face-api models:", err);
    }
  };

  detectFace = async () => {
    if (
      !this.faceApiLoaded ||
      !this.faceapi ||
      !this.webcamRef.current?.video ||
      this.state.capturedPhoto // stop detecting once photo is captured
    )
      return;

    const video = this.webcamRef.current.video;
    if (video.readyState !== 4) return;

    try {
      const rawDetections = await this.faceapi
        .detectAllFaces(video, new this.faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks();

      // Filter out invalid detections with null bounding box values
      // This prevents the "Box.constructor - expected box to be IBoundingBox" error
      const detections = rawDetections.filter((d) => {
        const box = d?.detection?.box || d?.detection?._box;
        if (!box) return false;
        return (
          box.x != null &&
          box.y != null &&
          box.width != null &&
          box.height != null &&
          !isNaN(box.x) &&
          !isNaN(box.y) &&
          !isNaN(box.width) &&
          !isNaN(box.height)
        );
      });

      // Draw detections on canvas overlay
      const canvas = this.canvasRef.current;
      if (canvas) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const displaySize = {
          width: video.videoWidth,
          height: video.videoHeight,
        };
        this.faceapi.matchDimensions(canvas, displaySize);

        const ctx = canvas.getContext("2d");
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Only draw if we have valid detections
        if (detections.length > 0) {
          try {
            const resizedDetections = this.faceapi.resizeResults(
              detections,
              displaySize
            );
            this.faceapi.draw.drawDetections(canvas, resizedDetections);
            this.faceapi.draw.drawFaceLandmarks(canvas, resizedDetections);
          } catch (drawErr) {
            // Silently ignore drawing errors - detection status still updates
            console.debug("Face overlay draw skipped:", drawErr.message);
          }
        }
      }

      if (detections.length > 0) {
        this.setState({ faceDetected: true });
        this.lastDetectedTime = Date.now();
      } else {
        // Grace period: only show "no face" after 2s without detection
        if (Date.now() - this.lastDetectedTime > 2000) {
          this.setState({ faceDetected: false });
        }
      }
    } catch (err) {
      // Silently handle detection errors - don't crash the page
      console.debug("Face detection cycle skipped:", err.message);
    }
  };

  startFaceDetection = () => {
    if (this.detectInterval) clearInterval(this.detectInterval);
    this.detectInterval = setInterval(this.detectFace, 300);
  };

  stopFaceDetection = () => {
    if (this.detectInterval) {
      clearInterval(this.detectInterval);
      this.detectInterval = null;
    }
  };

  /* ============================
     FACE DETECTION ON CAPTURED IMAGE (static)
     ============================ */

  detectFaceOnImage = async (imageSrc) => {
    if (!this.faceApiLoaded || !this.faceapi) {
      // If models aren't loaded, assume face is present (same as reference)
      this.setState({ faceDetected: true, faceDetectionLoading: false });
      return true;
    }
    this.setState({ faceDetectionLoading: true });
    try {
      const img = new Image();
      img.src = imageSrc;
      await new Promise((resolve) => {
        img.onload = resolve;
      });
      const rawDetections = await this.faceapi.detectAllFaces(
        img,
        new this.faceapi.TinyFaceDetectorOptions()
      );
      // Filter out detections with null bounding boxes
      const detections = rawDetections.filter((d) => {
        const box = d?.box || d?._box;
        if (!box) return false;
        return box.x != null && box.y != null && box.width != null && box.height != null;
      });
      const detected = detections.length > 0;
      this.setState({ faceDetected: detected, faceDetectionLoading: false });
      return detected;
    } catch (err) {
      console.error("Face detection on image error:", err);
      this.setState({ faceDetectionLoading: false, faceDetected: true });
      return true;
    }
  };

  /* ============================
     PHOTO CAPTURE & UPLOAD
     ============================ */

  capturePhoto = async () => {
    if (this.webcamRef.current) {
      const imageSrc = this.webcamRef.current.getScreenshot();
      if (imageSrc) {
        this.setState({ capturedPhoto: imageSrc });
        this.stopFaceDetection(); // Stop live detection after capture
        await this.detectFaceOnImage(imageSrc); // Validate captured image
      }
    }
  };

  retakePhoto = () => {
    this.setState({
      capturedPhoto: null,
      faceDetected: false,
      uploadError: "",
    });
    this.lastDetectedTime = Date.now();
    this.startFaceDetection(); // Resume live face detection
  };

  /**
   * Upload photo - aligned with Next.js reference:
   * - Endpoint: /BrokerAdminApi/finance-hub/uploadUserPhoto
   * - FormData fields: token, uuid, userPhoto
   * - Response: { error: { error_data: 0, error_msg: "" } }
   */
  uploadPhoto = () => {
    if (!this.state.capturedPhoto) {
      showToast("error", "Please capture a photo first");
      return;
    }
    if (!this.state.faceDetected) {
      showToast(
        "error",
        "No face detected. Please retake with your face clearly visible."
      );
      return;
    }
    if (!this.state.uuid || !this.state.token) {
      showToast("error", "Cannot identify the user. Please scan the QR code again.");
      return;
    }

    this.setState({ showLoader: true, uploadError: "" });

    // Convert base64 to blob
    const base64 = this.state.capturedPhoto;
    const byteString = atob(base64.split(",")[1]);
    const mimeString = base64.split(",")[0].split(":")[1].split(";")[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: mimeString });

    // FormData fields match Next.js reference
    let formData = new FormData();
    formData.append("token", this.state.token);
    formData.append("uuid", this.state.uuid);
    formData.append("userPhoto", blob, "userPhoto.png");

    // Endpoint and auth match Next.js reference
    fetch(WEBSERVICE + "/finance-hub/uploadUserPhoto", {
      method: "POST",
      headers: {
        Authorization: "BEARER " + this.state.token,
      },
      body: formData,
    })
      .then((response) => response.json())
      .then(
        function (data) {
          this.setState({ showLoader: false });
          // Response check matches Next.js reference: error.error_data === 0 means success
          if (data && data.error && data.error.error_data === 0) {
            this.setState({ uploadSuccess: true });
          } else {
            this.setState({
              uploadError:
                data?.error?.error_msg || "Upload failed. Please try again.",
            });
          }
        }.bind(this)
      )
      .catch(
        function (err) {
          this.setState({
            showLoader: false,
            uploadError:
              "Network error. Please check your connection and try again.",
          });
          console.error("Upload error:", err);
        }.bind(this)
      );
  };

  /* ============================
     RENDER
     ============================ */

  render() {
    const videoConstraints = {
      width: { ideal: 720 },
      height: { ideal: 960 },
      facingMode: "user",
    };

    return (
      <Fragment>
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            minHeight: "100vh",
            backgroundColor: "#f5f7fa",
            padding: "20px",
          }}
        >
          <div
            className="card shadow-sm"
            style={{ maxWidth: "500px", width: "100%" }}
          >
            <div className="card-body">
              {/* Header */}
              <div className="text-center mb-4">
                {/* {LOGO && (
                  <img
                    src={LOGO}
                    alt="Logo"
                    style={{ maxHeight: "40px", marginBottom: "12px" }}
                  />
                )} */}
                <h5>Photo Verification</h5>
                <p className="text-muted" style={{ fontSize: "14px" }}>
                  Take a clear selfie for identity verification
                </p>
              </div>

              {/* Invalid Params */}
              {!this.state.paramsValid && this.state.paramsError && (
                <div className="text-center py-4">
                  <i
                    className="fas fa-exclamation-triangle text-danger mb-3"
                    style={{ fontSize: "48px" }}
                  />
                  <p className="text-danger">{this.state.paramsError}</p>
                  <button
                    className="btn btn-outline-danger mt-2"
                    onClick={() => window.location.reload()}
                  >
                    <i className="fas fa-redo mr-1" />
                    Try Again
                  </button>
                </div>
              )}

              {/* Upload Success */}
              {this.state.uploadSuccess && (
                <div className="text-center py-4">
                  <i
                    className="fas fa-check-circle text-success mb-3"
                    style={{ fontSize: "64px" }}
                  />
                  <h5 className="text-success">Photo Uploaded!</h5>
                  <p className="text-muted">
                    Your photo has been uploaded successfully. You can close this
                    page and continue on your desktop.
                  </p>
                </div>
              )}

              {/* Camera / Capture */}
              {this.state.paramsValid && !this.state.uploadSuccess && (
                <div className="text-center">
                  {/* Live Camera */}
                  {!this.state.capturedPhoto && (
                    <div>
                      <div
                        className="kyc-camera-frame position-relative d-inline-block mb-3"
                      >
                        <Webcam
                          ref={this.webcamRef}
                          audio={false}
                          screenshotFormat="image/png"
                          videoConstraints={videoConstraints}
                          style={{ width: "100%", display: "block" }}
                          onUserMedia={() =>
                            this.setState({ cameraReady: true })
                          }
                          onUserMediaError={() => {
                            showToast(
                              "error",
                              "Camera access denied. Please allow camera access and reload."
                            );
                          }}
                        />
                        {/* Face detection canvas overlay */}
                        <canvas
                          ref={this.canvasRef}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            pointerEvents: "none",
                          }}
                        />
                        <div className="kyc-face-oval" />

                        {/* Face detection status overlays */}
                        {!this.state.faceDetected && this.faceApiLoaded && (
                          <div
                            style={{
                              position: "absolute",
                              top: "12px",
                              left: "12px",
                              right: "12px",
                              padding: "8px 12px",
                              borderRadius: "8px",
                              backgroundColor: "rgba(220, 53, 69, 0.9)",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              backdropFilter: "blur(8px)",
                              fontSize: "13px",
                              fontWeight: 600,
                            }}
                          >
                            <i className="fas fa-exclamation-triangle" />
                            No face detected. Please adjust your position.
                          </div>
                        )}

                        {this.state.faceDetected && this.faceApiLoaded && (
                          <div
                            style={{
                              position: "absolute",
                              bottom: "12px",
                              left: "12px",
                              padding: "6px 12px",
                              borderRadius: "8px",
                              backgroundColor: "rgba(40, 167, 69, 0.9)",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              backdropFilter: "blur(8px)",
                              fontSize: "12px",
                              fontWeight: 600,
                            }}
                          >
                            <span
                              style={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                backgroundColor: "#fff",
                                animation: "pulse 1.5s ease-in-out infinite",
                              }}
                            />
                            Face Detected
                          </div>
                        )}
                      </div>
                      <p
                        className="text-muted mb-3"
                        style={{ fontSize: "13px" }}
                      >
                        Position your face within the oval guide
                      </p>
                      {this.state.faceDetected ? (
                        <button
                          className="btn btn-primary btn-lg"
                          onClick={this.capturePhoto}
                          disabled={!this.state.cameraReady}
                          style={{ width: "100%", maxWidth: "300px" }}
                        >
                          <i className="fas fa-camera mr-2" />
                          Capture Photo
                        </button>
                      ) : (
                        <button
                          className="btn btn-secondary btn-lg"
                          disabled
                          style={{ width: "100%", maxWidth: "300px" }}
                        >
                          <i className="fas fa-exclamation-triangle mr-2" />
                          Position Your Face
                        </button>
                      )}
                    </div>
                  )}

                  {/* Captured Photo Preview */}
                  {this.state.capturedPhoto && (
                    <div>
                      <div className="kyc-photo-preview d-inline-block mb-3 position-relative">
                        <img
                          src={this.state.capturedPhoto}
                          alt="Captured selfie"
                          style={{ width: "100%", display: "block" }}
                        />
                        {/* Check icon overlay */}
                        <div
                          style={{
                            position: "absolute",
                            top: "12px",
                            right: "12px",
                            width: "36px",
                            height: "36px",
                            borderRadius: "50%",
                            backgroundColor: "#28a745",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <i className="fas fa-check" style={{ color: "#fff", fontSize: "16px" }} />
                        </div>
                      </div>

                      {this.state.faceDetectionLoading && (
                        <p className="text-info">
                          <i className="fas fa-spinner fa-spin mr-1" />
                          Detecting face...
                        </p>
                      )}
                      {!this.state.faceDetectionLoading &&
                        this.state.faceDetected && (
                          <p className="text-success">
                            <i className="fas fa-check-circle mr-1" />
                            Face detected successfully
                          </p>
                        )}
                      {!this.state.faceDetectionLoading &&
                        !this.state.faceDetected && (
                          <p className="text-danger">
                            <i className="fas fa-exclamation-circle mr-1" />
                            No face detected. Please retake the photo.
                          </p>
                        )}

                      {this.state.uploadError && (
                        <div className="alert alert-danger" role="alert">
                          {this.state.uploadError}
                        </div>
                      )}

                      <div className="mt-3">
                        <button
                          className="btn btn-secondary mr-2"
                          onClick={this.retakePhoto}
                          style={{ minWidth: "120px" }}
                        >
                          <i className="fas fa-redo mr-1" />
                          Take Another
                        </button>
                        <button
                          className="btn btn-primary"
                          onClick={this.uploadPhoto}
                          disabled={
                            !this.state.faceDetected ||
                            this.state.faceDetectionLoading
                          }
                          style={{ minWidth: "140px" }}
                        >
                          <i className="fas fa-upload mr-1" />
                          Submit Photo
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tips */}
              {this.state.paramsValid && !this.state.uploadSuccess && (
                <div className="kyc-tips-box">
                  <small className="fw-bold d-block mb-2">
                    📸 Photo Tips:
                  </small>
                  <small className="text-muted d-block">
                    - Ensure good lighting on your face
                  </small>
                  <small className="text-muted d-block">
                    - Remove sunglasses and hats
                  </small>
                  <small className="text-muted d-block">
                    - Look directly at the camera
                  </small>
                  <small className="text-muted d-block">
                    - Keep a neutral expression
                  </small>
                </div>
              )}
            </div>
          </div>
        </div>

        <Loader show={this.state.showLoader} />
      </Fragment>
    );
  }
}

export default KycCapturePhoto;